import { z } from "zod";

export const agentCapabilitiesSchema = z.object({
	create_sessions: z.boolean(),
	approve_verified_leads: z.boolean(),
	approve_outbound_actions: z.boolean(),
});

export const availableAgentSchema = z.object({
	key: z.string().min(1),
	display_name: z.string().min(1),
	description: z.string().min(1).optional(),
	team_size: z.number().int().positive().optional(),
	status: z.enum(["available", "unavailable"]),
	capabilities: agentCapabilitiesSchema,
});

export const availableAgentsSchema = z.array(availableAgentSchema);

export const sessionActorSchema = z.object({
	id: z.number().int().nullable(),
	name: z.string().nullable(),
});

const normalizedEventBaseSchema = z.object({
	id: z.string().min(1),
	author: z.string().min(1),
	created_at: z.string().datetime({ offset: true }).nullable(),
});

const messageEventSchema = normalizedEventBaseSchema.extend({
	type: z.literal("message"),
	content: z.object({
		text: z.string(),
	}),
});

const agentStatusEventSchema = normalizedEventBaseSchema.extend({
	type: z.literal("agent_status"),
	content: z.object({
		status: z.string(),
		label: z.string(),
	}),
});

const toolEventContentSchema = z.object({
	tool: z.string(),
	label: z.string(),
	status: z.string(),
});

export const approvalDecisionSchema = z.enum([
	"approved",
	"rejected",
	"revision_requested",
]);

export const approvalContinuationStatusSchema = z.enum([
	"pending",
	"completed",
	"failed",
]);

export const verifiedTargetSchema = z.object({
	target_id: z.string().min(1),
	lead_label: z.string().min(1),
	channel: z.enum(["email", "whatsapp", "phone", "linkedin"]),
	value: z.string().min(1),
	lead_id: z.string().min(1).nullable().optional(),
	person_id: z.string().min(1).nullable().optional(),
	person_name: z.string().min(1).nullable().optional(),
	role_title: z.string().min(1).nullable().optional(),
	segment: z.string().min(1).nullable().optional(),
	persona_code: z.string().min(1).nullable().optional(),
	persona_classification: z.string().min(1).nullable().optional(),
	qualification_score: z.number().int().min(0).max(100).nullable().optional(),
	rationale: z.array(z.string()).optional(),
	evidence_urls: z.array(z.string()).optional(),
	red_flags: z.array(z.string()).optional(),
});

const approvalProposalBaseSchema = z.object({
	approval_id: z.string().min(1),
	status: z.literal("pending"),
	title: z.string().min(1),
});

export const verifiedLeadApprovalProposalSchema =
	approvalProposalBaseSchema.extend({
		approval_type: z.literal("verified_leads"),
		targets: z.array(verifiedTargetSchema).min(1),
	});

export const outboundActionApprovalProposalSchema =
	approvalProposalBaseSchema.extend({
		approval_type: z.literal("outbound_action"),
		action_id: z.string().min(1),
		action_fingerprint: z.string().regex(/^[a-f0-9]{64}$/),
		channel: z.enum([
			"email",
			"whatsapp",
			"phone",
			"linkedin",
			"linkedin_connection",
		]),
		recipient: z.string().min(1),
		subject: z.string().nullable(),
		content_preview: z.string().min(1),
	});

export const approvalProposalSchema = z.discriminatedUnion("approval_type", [
	verifiedLeadApprovalProposalSchema,
	outboundActionApprovalProposalSchema,
]);

export const approvalDecisionPayloadSchema = z
	.object({
		approval_id: z.string().min(1),
		decision: approvalDecisionSchema,
		selected_target_ids: z.array(z.string().min(1)).default([]),
		revision_notes: z.string().trim().max(2_000).nullable().default(null),
	})
	.strict()
	.superRefine((payload, context) => {
		if (payload.decision === "revision_requested" && !payload.revision_notes) {
			context.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["revision_notes"],
				message: "Explain what should be changed.",
			});
		}
	});

export const structuredApprovalRecordedContentSchema = z.object({
	approval_id: z.string().min(1),
	approval_type: z.enum(["verified_leads", "outbound_action"]),
	decision: approvalDecisionSchema,
	status: approvalDecisionSchema,
	label: z.string().min(1),
	selected_target_ids: z.array(z.string()),
	revision_notes: z.string().nullable(),
	continuation_status: approvalContinuationStatusSchema,
	decided_by: sessionActorSchema,
	decided_at: z.string().datetime({ offset: true }),
});

const toolStartedEventSchema = normalizedEventBaseSchema.extend({
	type: z.literal("tool_started"),
	content: toolEventContentSchema,
});

const toolCompletedEventSchema = normalizedEventBaseSchema.extend({
	type: z.literal("tool_completed"),
	content: toolEventContentSchema,
});

const approvalRequiredEventSchema = normalizedEventBaseSchema.extend({
	type: z.literal("approval_required"),
	content: z.union([
		approvalProposalSchema,
		z.object({
			label: z.string().optional(),
			status: z.string().optional(),
		}),
	]),
});

const approvalRecordedEventSchema = normalizedEventBaseSchema.extend({
	type: z.literal("approval_recorded"),
	content: z.union([
		structuredApprovalRecordedContentSchema,
		toolEventContentSchema,
	]),
});

const requestInputRequiredContentSchema = z.object({
	interrupt_id: z.string().min(1),
	message: z.string().min(1),
	payload: z.unknown().nullable(),
	response_schema: z.record(z.unknown()).nullable(),
	status: z.literal("pending"),
});

const inputRequiredEventSchema = normalizedEventBaseSchema.extend({
	type: z.literal("input_required"),
	content: requestInputRequiredContentSchema,
});

const inputRecordedEventSchema = normalizedEventBaseSchema.extend({
	type: z.literal("input_recorded"),
	content: z.object({
		interrupt_id: z.string().min(1),
		response: z.unknown(),
		status: z.literal("answered"),
	}),
});

const artifactEventSchema = normalizedEventBaseSchema.extend({
	type: z.literal("artifact"),
	content: z.object({
		mime_type: z.string(),
		label: z.string(),
	}),
});

const errorEventSchema = normalizedEventBaseSchema.extend({
	type: z.literal("error"),
	content: z.object({
		message: z.string(),
	}),
});

export const normalizedEventSchema = z.discriminatedUnion("type", [
	messageEventSchema,
	agentStatusEventSchema,
	toolStartedEventSchema,
	toolCompletedEventSchema,
	approvalRequiredEventSchema,
	approvalRecordedEventSchema,
	inputRequiredEventSchema,
	inputRecordedEventSchema,
	artifactEventSchema,
	errorEventSchema,
]);

export const agentSessionSchema = z.object({
	id: z.string().min(1),
	agent_key: z.string().min(1),
	title: z.string(),
	status: z.string().min(1),
	created_by: sessionActorSchema,
	created_at: z.string().datetime({ offset: true }),
	updated_at: z.string().datetime({ offset: true }),
	events: z.array(normalizedEventSchema).optional().default([]),
});

export const agentSessionsSchema = z.array(agentSessionSchema);

export const messageResponseSchema = z.object({
	session_id: z.string().min(1),
	events: z.array(normalizedEventSchema),
});

export const requestInputResponsePayloadSchema = z
	.object({
		response: z.unknown(),
	})
	.strict();

export const messageDeltaStreamDataSchema = z.object({
	session_id: z.string().min(1),
	message_key: z.string().min(1),
	delta: z.string(),
});

export const agentEventStreamDataSchema = z.object({
	session_id: z.string().min(1),
	message_key: z.string().min(1).optional(),
	event: normalizedEventSchema,
});

export const completeStreamDataSchema = z.object({
	session_id: z.string().min(1),
	correlation_id: z.string().min(1),
});

export const errorStreamDataSchema = z.object({
	session_id: z.string().min(1),
	type: z.literal("error"),
	code: z.string().min(1),
	message: z.string().min(1),
	correlation_id: z.string().min(1),
});

export const approvalRecordSchema = z.object({
	approval_id: z.string().min(1),
	approval_type: z.enum(["verified_leads", "outbound_action"]),
	decision: approvalDecisionSchema,
	status: z.enum(["approved", "rejected", "revision_requested", "stale"]),
	selected_target_ids: z.array(z.string()),
	revision_notes: z.string().nullable(),
	continuation_status: approvalContinuationStatusSchema,
	decided_by: sessionActorSchema,
	decided_at: z.string().datetime({ offset: true }),
	events: z.array(normalizedEventSchema),
});

export const createAgentSessionPayloadSchema = z.object({
	session_name: z.string().trim().min(1).max(255).optional(),
	initial_context: z
		.object({
			campaign_id: z.string().min(1).optional(),
			campaign: z
				.object({
					target_segments: z
						.array(
							z.enum([
								"P1_HOSPITALITY",
								"P2_FOOD_SERVICE",
								"P3_PRIVATE_LABEL_INSTITUTIONAL",
							]),
						)
						.min(1)
						.max(3),
					requested_company_count: z.number().int().min(1).max(100),
				})
				.optional(),
		})
		.optional(),
});

export type AgentCapabilities = z.infer<typeof agentCapabilitiesSchema>;
export type AvailableAgent = z.infer<typeof availableAgentSchema>;
export type NormalizedEvent = z.infer<typeof normalizedEventSchema>;
export type AgentSession = z.infer<typeof agentSessionSchema>;
export type MessageResponse = z.infer<typeof messageResponseSchema>;
export type AgentStreamFrame =
	| {
			event: "message_delta";
			id?: string;
			data: z.infer<typeof messageDeltaStreamDataSchema>;
	  }
	| {
			event: "agent_event";
			id?: string;
			data: z.infer<typeof agentEventStreamDataSchema>;
	  }
	| {
			event: "complete";
			id?: string;
			data: z.infer<typeof completeStreamDataSchema>;
	  }
	| {
			event: "error";
			id?: string;
			data: z.infer<typeof errorStreamDataSchema>;
	  };
export type CreateAgentSessionPayload = z.infer<
	typeof createAgentSessionPayloadSchema
>;
export type ApprovalProposal = z.infer<typeof approvalProposalSchema>;
export type RequestInputRequiredContent = z.infer<
	typeof requestInputRequiredContentSchema
>;
export type RequestInputResponsePayload = z.infer<
	typeof requestInputResponsePayloadSchema
>;
export type VerifiedLeadApprovalProposal = z.infer<
	typeof verifiedLeadApprovalProposalSchema
>;
export type OutboundActionApprovalProposal = z.infer<
	typeof outboundActionApprovalProposalSchema
>;
export type ApprovalDecisionPayload = z.infer<
	typeof approvalDecisionPayloadSchema
>;
export type ApprovalRecord = z.infer<typeof approvalRecordSchema>;
export type StructuredApprovalRecordedContent = z.infer<
	typeof structuredApprovalRecordedContentSchema
>;

export function isAvailableAgent(agent: AvailableAgent) {
	return agent.status === "available";
}
